/**
 * Mesaj yazma alanı: metin, dosya eki, sesle yazdırma, gönder/durdur.
 *
 * Enter gönderir, Shift+Enter satır atlar (sohbet arayüzlerinin ortak dili). Alan
 * içerikle birlikte büyüyor ama bir tavanı var: uzun yapıştırmalarda sohbeti ekrandan
 * itmesin.
 *
 * Mikrofon tarayıcının konuşma tanımasını kullanıyor (Web Speech API). Desteklenmeyen
 * tarayıcıda düğme HİÇ çıkmıyor — çalışmayan bir düğme göstermek, bozuk olduğunu
 * sanmaya yol açıyor.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Loader2, Mic, MicOff, Paperclip, Square, X } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { IconButton } from '@/shared/ui/IconButton';
import { toast } from '@/shared/ui/toastStore';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import type { AiAttachmentRef } from '@/shared/types/aiChat';
import { uploadAiAttachment } from '../api/aiApi';
import { useAiStore } from '../model/aiStore';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
}

function speechRecognition(): SpeechRecognitionLike | null {
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  const Ctor = w.SpeechRecognition ?? w.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export function AiComposer({
  disabled,
  onSend,
  onStop,
}: {
  disabled: boolean;
  onSend: (text: string, attachments: AiAttachmentRef[]) => void;
  onStop: () => void;
}) {
  const { t } = useTranslation();
  const language = usePrefsStore((s) => s.prefs.language);
  const streaming = useAiStore((s) => s.streaming);
  const pending = useAiStore((s) => s.pendingAttachments);
  const setPending = useAiStore((s) => s.setPendingAttachments);
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [listening, setListening] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const [micSupported] = useState(() => !!speechRecognition());

  // Alan içerikle büyür; tavanı aşınca kendi içinde kaydırılır.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [text]);

  const submit = () => {
    const value = text.trim();
    if (!value || disabled || streaming) return;
    onSend(value, pending);
    setText('');
  };

  const attach = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      const uploaded: AiAttachmentRef[] = [];
      for (const file of Array.from(files)) {
        if (file.size > MAX_ATTACHMENT_BYTES) {
          toast.warning(t('ai.attachTooLarge', { name: file.name }));
          continue;
        }
        uploaded.push(await uploadAiAttachment(file));
      }
      if (uploaded.length) setPending([...pending, ...uploaded]);
    } catch (error) {
      toast.error(t('ai.attachFailed'), error instanceof Error ? error.message : String(error));
    } finally {
      setUploading(false);
    }
  };

  const toggleMic = () => {
    if (listening) {
      recognitionRef.current?.stop();
      return;
    }
    const recognition = speechRecognition();
    if (!recognition) return;
    recognition.lang = language || 'tr-TR';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i]?.[0]?.transcript ?? '';
      }
      if (transcript) setText((current) => (current ? `${current} ${transcript}` : transcript));
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    setListening(true);
    recognition.start();
  };

  return (
    <div className="border-border bg-bg-elevated border-t p-2">
      {pending.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-1">
          {pending.map((attachment) => (
            <span
              key={attachment.filePath}
              className="border-border bg-bg-muted inline-flex max-w-full items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]"
            >
              <Paperclip className="h-3 w-3 shrink-0" />
              <span className="max-w-[12rem] truncate">{attachment.fileName}</span>
              <button
                type="button"
                aria-label={t('ai.removeAttachment')}
                className="text-fg-subtle hover:text-fg"
                onClick={() => setPending(pending.filter((a) => a.filePath !== attachment.filePath))}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      ) : null}

      <div
        className={cn(
          'border-border bg-bg focus-within:border-brand-500 flex items-end gap-1 rounded-xl border px-2 py-1.5 transition-colors',
          disabled && 'opacity-60',
        )}
      >
        <IconButton
          label={t('ai.attach')}
          size="sm"
          disabled={disabled || uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
        </IconButton>
        <input
          ref={fileRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void attach(e.target.files);
            e.target.value = '';
          }}
        />

        <textarea
          ref={textareaRef}
          rows={1}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
          placeholder={disabled ? t('ai.composerDisabled') : t('ai.composerPlaceholder')}
          aria-label={t('ai.composerPlaceholder')}
          className="text-fg placeholder:text-fg-subtle max-h-[180px] min-h-[24px] flex-1 resize-none bg-transparent py-1 text-sm outline-none"
        />

        {micSupported ? (
          <IconButton
            label={listening ? t('ai.micStop') : t('ai.mic')}
            size="sm"
            disabled={disabled}
            onClick={toggleMic}
            className={cn(listening && 'text-danger')}
          >
            {listening ? <MicOff className="h-4 w-4 animate-pulse" /> : <Mic className="h-4 w-4" />}
          </IconButton>
        ) : null}

        {streaming ? (
          <IconButton label={t('ai.stop')} size="sm" onClick={onStop} className="text-danger">
            <Square className="h-4 w-4 fill-current" />
          </IconButton>
        ) : (
          <button
            type="button"
            aria-label={t('ai.send')}
            title={t('ai.send')}
            disabled={disabled || !text.trim()}
            onClick={submit}
            className="bg-brand-600 hover:bg-brand-700 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-white transition-colors disabled:opacity-40"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
        )}
      </div>

      <p className="text-fg-subtle mt-1 text-center text-[10px]">{t('ai.disclaimer')}</p>
    </div>
  );
}
